<script setup lang="ts">
import { computed } from 'vue';
import { iSelect, iStepper } from '@inscribe/signal';
import {
  type TimeframeFilter,
  // TimeFieldFrequencyType, // FUTURE: enable when backend supports frequency
  TimeframeMode,
} from '@/types/decision-engine/configurable-insights.types';
import FilterEditorLayout from '@/V2/decision-engine/configurable-insights/components/filters/FilterEditorLayout.vue';

interface Props {
  filter: TimeframeFilter | null;
}

interface Emits {
  (e: 'update:filter', value: TimeframeFilter): void;
  (e: 'remove'): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// FUTURE: frequency options — enable when backend supports frequency for timeframe filters
// const frequencyOptions = [
//   { text: 'None', value: null },
//   { text: 'Daily', value: TimeFieldFrequencyType.DAILY },
//   { text: 'Weekly', value: TimeFieldFrequencyType.WEEKLY },
//   { text: 'Fortnightly', value: TimeFieldFrequencyType.FORTNIGHTLY },
//   { text: 'Monthly', value: TimeFieldFrequencyType.MONTHLY },
//   { text: 'Yearly', value: TimeFieldFrequencyType.YEARLY },
// ];

// Mode options
const modeOptions = [
  { text: 'over a window of', value: TimeframeMode.SLIDING_WINDOW },
  { text: 'in the last', value: TimeframeMode.MOST_RECENT_ACCOUNT_PERIOD },
];

// Current filter or default
// Always ensure mode is present, even if filter is partially defined
const currentFilter = computed<TimeframeFilter>(() => {
  const defaults: TimeframeFilter = {
    time_period_in_days: null,
    mode: TimeframeMode.SLIDING_WINDOW,
  };

  if (!props.filter) {
    return defaults;
  }

  // Merge with defaults to ensure all required fields are present
  return {
    ...defaults,
    ...props.filter,
    // Ensure mode is always set
    mode: props.filter.mode || TimeframeMode.SLIDING_WINDOW,
  };
});

// Update handlers
// FUTURE: const updateFrequency = (frequency: TimeFieldFrequencyType | null) => {
//   emit('update:filter', { ...currentFilter.value, frequency });
// };

const updateTimePeriod = (days: number) => {
  emit('update:filter', {
    ...currentFilter.value,
    time_period_in_days: days > 0 ? days : null,
  });
};

const updateMode = (mode: TimeframeMode) => {
  emit('update:filter', {
    ...currentFilter.value,
    mode,
  });
};
</script>

<template>
  <FilterEditorLayout label="Timeframe" @remove="emit('remove')">
    <!-- Mode -->
    <div class="filter-section">
      <i-select
        :model-value="currentFilter.mode"
        :options="modeOptions"
        @update:model-value="updateMode"
      />
    </div>

    <!-- FUTURE: frequency selector — enable when backend supports frequency for timeframe filters -->
    <!-- <div class="filter-section">
      <i-select
        :model-value="currentFilter.frequency"
        :options="frequencyOptions"
        @update:model-value="updateFrequency"
      />
    </div> -->

    <!-- Time Period -->
    <div class="filter-section d-flex align-items-center i-gap-6">
      <i-stepper
        :model-value="currentFilter.time_period_in_days ?? 0"
        :max-value="Infinity"
        :min-value="0"
        :full-width="true"
        :precision="0"
        :enabled="true"
        size="medium"
        @update:model-value="updateTimePeriod"
      />
      <span class="i-text-body-2 time-period-label">days</span>
    </div>
  </FilterEditorLayout>
</template>

<style scoped lang="scss">
.time-period-label {
  color: var(--text-neutral-bold);
  white-space: nowrap;
}
</style>
